import { useEffect, useState, useTransition } from "react";
import { useRevalidate } from "@/lib/loader";
import type { Invitation } from "@/lib/api/types";
import { listMyInvitations } from "@/lib/api/endpoints";
import { acceptInvitationAction } from "@/lib/api/invitations";

/**
 * Dismissible top-of-page banner shown when the signed-in user has pending
 * workspace invitations. Fetches invitations client-side on mount, so the
 * server layout never blocks on it.
 *
 * The banner shows the first pending invite by name with Accept / Dismiss
 * affordances. If there are multiple invites it shows "and N more". Accepting
 * calls the existing `acceptInvitationAction` server action (which writes the
 * active-workspace cookie server-side) and then refreshes the router.
 */
export function PendingInvitesBanner() {
  const revalidate = useRevalidate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [pending, startTransition] = useTransition();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyInvitations()
      .then((list) => {
        if (!cancelled) setInvitations(list);
      })
      .catch(() => {
        // Silently ignore — banner is non-critical.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (dismissed || invitations.length === 0) return null;

  const first = invitations[0];
  const rest = invitations.length - 1;

  function handleAccept() {
    setError(null);
    setBusyId(first.workspaceId);
    startTransition(async () => {
      try {
        const result = await acceptInvitationAction(first.workspaceId);
        if (!result.ok) {
          setError(result.error ?? "Could not accept invitation.");
          setBusyId(null);
          return;
        }
        // Remove this invite from our local list; if none remain, banner disappears.
        setInvitations((prev) =>
          prev.filter((i) => i.workspaceId !== first.workspaceId),
        );
        setBusyId(null);
        revalidate();
      } catch {
        setError("Something went wrong.");
        setBusyId(null);
      }
    });
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm dark:border-amber-900/40 dark:bg-amber-950/20"
    >
      <div className="flex items-center gap-2">
        <MailIcon />
        <span className="text-zinc-800 dark:text-zinc-200">
          You&apos;ve been invited to{" "}
          <span className="font-semibold">{first.workspaceName}</span>
          {rest > 0 && (
            <span className="text-zinc-500 dark:text-zinc-400">
              {" "}and {rest} more
            </span>
          )}
        </span>
        {error && (
          <span className="ml-1 text-xs text-red-600 dark:text-red-400">
            {error}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          disabled={pending}
          onClick={handleAccept}
          className="inline-flex items-center gap-1.5 rounded border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-700 dark:bg-surface dark:text-amber-300 dark:hover:bg-amber-950/30"
        >
          {busyId === first.workspaceId ? <SpinnerIcon /> : null}
          Accept
        </button>
        <button
          type="button"
          aria-label="Dismiss invitation banner"
          onClick={() => setDismissed(true)}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-surface-2 dark:hover:text-zinc-300"
        >
          <XIcon />
        </button>
      </div>
    </div>
  );
}

function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-amber-500"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="animate-spin shrink-0"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
